import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Receipt, Send, CheckCircle2, FileDown, Pencil, Car, Search, Layers, CalendarPlus } from "lucide-react";
import { api } from "../../lib/api";
import { F, Skeleton, useConfirm } from "./_shared";

/* ---------------- Invoices Tab ---------------- */
const INV_STATUS = {
  draft:     { label: "Draft",     cls: "bg-white/10 text-white/70 border-white/20" },
  sent:      { label: "Sent",      cls: "bg-cyan/15 text-cyan border-cyan/40" },
  paid:      { label: "Paid",      cls: "bg-emerald-400/15 text-emerald-300 border-emerald-400/40" },
  cancelled: { label: "Cancelled", cls: "bg-red-500/15 text-red-300 border-red-500/40" },
};
const PAYMENT_METHODS = ["cash", "zelle", "paypal", "apple_pay", "stripe", "other"];

export default function InvoicesTab() {
  const [invoices, setInvoices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [fromRentals, setFromRentals] = useState(false);
  const [loading, setLoading] = useState(true);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/invoices")
      .then((r) => setInvoices(r.data))
      .catch(() => toast.error("Failed to load invoices"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/admin/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `plenty-rydez-${inv.invoice_number || inv.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF failed");
    }
  };

  const send = async (inv) => {
    if (!inv.customer_email) return toast.error("Add a customer email first");
    const ok = await confirm({
      title: "Email this invoice?",
      message: `A branded PDF invoice will be delivered to the customer via Resend.`,
      meta: `${inv.invoice_number} → ${inv.customer_email}`,
      confirmLabel: "Send invoice",
      tone: "info",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/admin/invoices/${inv.id}/send`);
      if (data.email_delivered) toast.success(`Invoice sent to ${data.to}`);
      else toast.warning(`Send failed — check RESEND config or verify plenty-rydez.com domain`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  const markPaid = async (inv) => {
    try {
      await api.patch(`/admin/invoices/${inv.id}`, { status: "paid" });
      toast.success("Marked as paid");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (inv) => {
    const ok = await confirm({
      title: "Delete this invoice?",
      message: "Removes it from the ledger. If the invoice was already emailed to the customer, they'll still have their copy.",
      meta: `${inv.invoice_number} · ${inv.customer_name || inv.customer_email || ""}`,
      confirmLabel: "Delete invoice",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/invoices/${inv.id}`);
      toast.success("Invoice deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const [converting, setConverting] = useState(null);   // invoice being converted

  return (
    <div data-testid="bo-invoices-tab" className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-caps text-cyan">Invoices · Cash & phone bookings</div>
          <div className="text-xs text-white/50 mt-1">
            Create branded invoices for customers who call in or pay outside the website. Email them directly from here.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button"
            data-testid="bo-new-invoice-from-rentals"
            onClick={() => setFromRentals(true)}
            className="border border-cyan/40 text-cyan rounded-full px-4 py-2 text-xs font-semibold label-caps hover:bg-cyan/10 inline-flex items-center gap-1.5">
            <Layers size={12} /> From rentals
          </button>
          <button type="button"
            data-testid="bo-new-invoice"
            onClick={() => setEditing({})}
            className="bg-cyan text-midnight rounded-full px-4 py-2 text-xs font-semibold label-caps hover:brightness-110 inline-flex items-center gap-1.5">
            <Plus size={12} /> New invoice
          </button>
        </div>
      </div>

      {fromRentals && (
        <FromRentalsModal
          onClose={() => setFromRentals(false)}
          onCreated={(inv) => { setFromRentals(false); load(); setEditing(inv); }}
        />
      )}

      {editing !== null && (
        <InvoiceModal
          invoice={editing?.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {loading ? (
        <Skeleton />
      ) : invoices.length === 0 ? (
        <div className="glass rounded-3xl p-10 text-center text-white/50">
          <Receipt size={32} className="mx-auto mb-3 text-cyan" />
          <div className="font-serif text-lg text-white mb-1">No invoices yet</div>
          <div className="text-xs">Create your first invoice for a phone booking above.</div>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="bo-invoices-list">
          {invoices.map((inv) => {
            const s = INV_STATUS[inv.status] || INV_STATUS.draft;
            const t = inv.totals || {};
            return (
              <div key={inv.id} data-testid={`bo-invoice-${inv.id}`}
                className="glass rounded-2xl p-4 flex items-center justify-between gap-4 flex-wrap border border-white/10 hover:border-cyan/40 transition">
                <div className="min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-cyan">{inv.invoice_number}</span>
                    <span className={`label-caps text-[10px] px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>
                    <span className="label-caps text-[9px] text-white/50">{inv.payment_method?.replace(/_/g, " ")}</span>
                  </div>
                  <div className="font-serif text-lg mt-0.5">{inv.customer_name}</div>
                  <div className="text-xs text-white/50">
                    {inv.customer_email || "no email"}{inv.customer_phone ? ` · ${inv.customer_phone}` : ""}
                  </div>
                  <div className="text-[11px] text-white/40 mt-1">
                    Created {new Date(inv.created_at).toLocaleDateString()} by {inv.created_by}
                    {inv.sent_at ? ` · sent ${new Date(inv.sent_at).toLocaleDateString()}` : ""}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-serif text-2xl">${(t.total || 0).toFixed(2)}</div>
                  <div className="text-[10px] text-white/40">{(inv.line_items || []).length} line item{(inv.line_items || []).length === 1 ? "" : "s"}</div>
                  {Number(inv.deposit_amount) > 0 && (
                    <div className="text-[10px] text-lime-300 mt-0.5">
                      + ${Number(inv.deposit_amount).toFixed(2)} deposit ({(inv.deposit_status || "pending").replace(/_/g," ")})
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button type="button" onClick={() => setEditing(inv)}
                    data-testid={`bo-invoice-edit-${inv.id}`}
                    className="px-3 py-1.5 rounded-full text-[10px] label-caps bg-cyan text-midnight font-semibold hover:brightness-110 inline-flex items-center gap-1">
                    <Pencil size={11} /> Edit & adjust price
                  </button>
                  <button type="button" onClick={() => downloadPdf(inv)}
                    data-testid={`bo-invoice-pdf-${inv.id}`}
                    className="px-3 py-1.5 rounded-full text-[10px] label-caps border border-cyan/40 text-cyan hover:bg-cyan/10 inline-flex items-center gap-1">
                    <FileDown size={11} /> PDF
                  </button>
                  <button type="button" onClick={() => send(inv)}
                    data-testid={`bo-invoice-send-${inv.id}`}
                    disabled={!inv.customer_email}
                    className="px-3 py-1.5 rounded-full text-[10px] label-caps border border-white/20 text-white/70 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1">
                    <Send size={11} /> Email
                  </button>
                  {inv.status !== "paid" && (
                    <button type="button" onClick={() => markPaid(inv)}
                      data-testid={`bo-invoice-paid-${inv.id}`}
                      className="px-3 py-1.5 rounded-full text-[10px] label-caps border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 inline-flex items-center gap-1">
                      <CheckCircle2 size={11} /> Paid
                    </button>
                  )}
                  <button type="button" onClick={() => setConverting(inv)}
                    data-testid={`bo-invoice-tobooking-${inv.id}`}
                    disabled={!inv.vehicle_id || !inv.service_date || !!inv.linked_booking_id}
                    title={
                      !inv.vehicle_id ? "Set a vehicle first"
                      : !inv.service_date ? "Set a service date first"
                      : inv.linked_booking_id ? "Already converted to a booking"
                      : "Convert this invoice into a live booking on the calendar"
                    }
                    className="px-3 py-1.5 rounded-full text-[10px] label-caps border border-amber-400/40 text-amber-200 hover:bg-amber-400/10 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1">
                    <CalendarPlus size={11} /> {inv.linked_booking_id ? "Booking created" : "→ Booking"}
                  </button>
                  <button type="button" onClick={() => remove(inv)}
                    data-testid={`bo-invoice-delete-${inv.id}`}
                    className="px-3 py-1.5 rounded-full text-[10px] label-caps border border-red-500/40 text-red-300 hover:bg-red-500/10">
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {confirmDialog}
      {converting && (
        <ConvertInvoiceModal
          invoice={converting}
          onClose={() => setConverting(null)}
          onDone={() => { setConverting(null); load(); }}
        />
      )}
    </div>
  );
}

function InvoiceModal({ invoice, onClose, onSaved }) {
  const isEdit = !!invoice;
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(() => ({
    customer_name: invoice?.customer_name || "",
    customer_email: invoice?.customer_email || "",
    customer_phone: invoice?.customer_phone || "",
    payment_method: invoice?.payment_method || "cash",
    tax_rate: invoice?.tax_rate ?? 8.875,
    discount: invoice?.discount ?? 0,
    due_date: invoice?.due_date || "",
    service_date: invoice?.service_date ? invoice.service_date.slice(0, 10) : "",
    service_time: invoice?.service_date ? invoice.service_date.slice(11, 16) : "10:00",
    vehicle_id: invoice?.vehicle_id || "",
    custom_vehicle_name: invoice?.custom_vehicle_name || "",
    pickup_location: invoice?.pickup_location || "",
    deposit_amount: invoice?.deposit_amount ?? 0,
    deposit_status: invoice?.deposit_status || "pending",
    deposit_note: invoice?.deposit_note || "",
    notes: invoice?.notes || "",
    line_items: invoice?.line_items?.length
      ? invoice.line_items.map((li) => ({ ...li, unit: li.unit || "hour" }))
      : [{ description: "", quantity: 1, unit_price: 0, unit: "hour" }],
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/admin/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
  }, []);

  // When user picks a vehicle: pre-fill the first line item's rate to that
  // vehicle's hourly rate + description if the line is still empty.
  const pickVehicle = (vid) => {
    set("vehicle_id", vid);
    const v = vehicles.find((x) => x.id === vid);
    if (v && form.line_items[0] && !form.line_items[0].description && !form.line_items[0].unit_price) {
      setLine(0, {
        description: `Chauffeured service · ${v.name}`,
        unit_price: v.hourly_rate,
      });
    }
  };

  const totals = React.useMemo(() => {
    const subtotal = form.line_items.reduce(
      (sum, li) => sum + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0), 0
    );
    const discount = Number(form.discount) || 0;
    const taxable = Math.max(0, subtotal - discount);
    const tax = taxable * ((Number(form.tax_rate) || 0) / 100);
    return { subtotal, discount, tax, total: taxable + tax };
  }, [form.line_items, form.discount, form.tax_rate]);

  const addLine = () => set("line_items", [...form.line_items, { description: "", quantity: 1, unit_price: 0, unit: "hour" }]);
  const removeLine = (idx) => set("line_items", form.line_items.filter((_, i) => i !== idx));
  const setLine = (idx, patch) => set("line_items",
    form.line_items.map((li, i) => (i === idx ? { ...li, ...patch } : li))
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    const lines = form.line_items
      .filter((li) => li.description?.trim())
      .map((li) => ({
        description: li.description.trim(),
        quantity: Number(li.quantity) || 0,
        unit_price: Number(li.unit_price) || 0,
        unit: li.unit === "day" ? "day" : "hour",
      }));
    if (lines.length === 0) return toast.error("Add at least one line item");
    setBusy(true);
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim() || null,
        customer_phone: form.customer_phone.trim() || null,
        payment_method: form.payment_method,
        tax_rate: Number(form.tax_rate) || 0,
        discount: Number(form.discount) || 0,
        due_date: form.due_date || null,
        vehicle_id: form.vehicle_id === "__other__" ? null : (form.vehicle_id || null),
        custom_vehicle_name: form.vehicle_id === "__other__"
          ? (form.custom_vehicle_name || "").trim() || null
          : null,
        service_date: form.service_date
          ? new Date(`${form.service_date}T${form.service_time || "10:00"}`).toISOString()
          : null,
        pickup_location: form.pickup_location.trim() || null,
        notes: form.notes?.trim() || null,
        line_items: lines,
        deposit_amount: Number(form.deposit_amount) || 0,
        deposit_status: form.deposit_status || "pending",
        deposit_note: form.deposit_note?.trim() || null,
      };
      if (isEdit) {
        await api.patch(`/admin/invoices/${invoice.id}`, payload);
        toast.success("Invoice updated");
      } else {
        await api.post("/admin/invoices", payload);
        toast.success("Invoice created");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="bo-invoice-modal"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-ink-950/85 flex items-start justify-center p-4 overflow-y-auto"
    >
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-3xl bg-ink-900 border border-cyan/40 rounded-3xl p-6 my-8 space-y-4">
        <div className="flex items-center justify-between">
          <div className="label-caps text-cyan text-xs">
            {isEdit ? `Edit ${invoice.invoice_number}` : "New invoice · cash / phone booking"}
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <F label="Customer name *">
            <input required data-testid="bo-invoice-name" value={form.customer_name}
              onChange={(e) => set("customer_name", e.target.value)} className="nv-input"
              placeholder="Jane Rivera" />
          </F>
          <F label="Payment method">
            <select data-testid="bo-invoice-method" value={form.payment_method}
              onChange={(e) => set("payment_method", e.target.value)} className="nv-input">
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>{m.replace(/_/g, " ")}</option>
              ))}
            </select>
          </F>
          <F label="Customer email">
            <input type="email" data-testid="bo-invoice-email" value={form.customer_email}
              onChange={(e) => set("customer_email", e.target.value)} className="nv-input"
              placeholder="jane@example.com" />
          </F>
          <F label="Customer phone">
            <input data-testid="bo-invoice-phone" value={form.customer_phone}
              onChange={(e) => set("customer_phone", e.target.value)} className="nv-input"
              placeholder="+1 917 555 1234" />
          </F>
          <F label="Vehicle used">
            <select data-testid="bo-invoice-vehicle" value={form.vehicle_id}
              onChange={(e) => pickVehicle(e.target.value)} className="nv-input">
              <option value="">— No vehicle —</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>{v.name} · ${v.hourly_rate}/hr · ${v.daily_rate}/day</option>
              ))}
              <option value="__other__">Other — type name below</option>
            </select>
          </F>
          {form.vehicle_id === "__other__" && (
            <F label="Other vehicle name">
              <input
                data-testid="bo-invoice-vehicle-other"
                value={form.custom_vehicle_name || ""}
                onChange={(e) => set("custom_vehicle_name", e.target.value)}
                className="nv-input"
                placeholder="e.g. Partner's 2025 Rolls Royce Cullinan" />
            </F>
          )}
          <F label="Pickup location">
            <input data-testid="bo-invoice-pickup" value={form.pickup_location}
              onChange={(e) => set("pickup_location", e.target.value)} className="nv-input"
              placeholder="JFK Terminal 4" />
          </F>
          <F label="Service date (shows on schedule)">
            <input type="date" data-testid="bo-invoice-service-date" value={form.service_date}
              onChange={(e) => set("service_date", e.target.value)} className="nv-input" />
          </F>
          <F label="Service time">
            <input type="time" data-testid="bo-invoice-service-time" value={form.service_time}
              onChange={(e) => set("service_time", e.target.value)} className="nv-input" />
          </F>
          <F label="Due date (optional)">
            <input type="date" data-testid="bo-invoice-due" value={form.due_date}
              onChange={(e) => set("due_date", e.target.value)} className="nv-input" />
          </F>
          <F label="Tax rate (%)">
            <input type="number" step="0.001" data-testid="bo-invoice-tax" value={form.tax_rate}
              onChange={(e) => set("tax_rate", e.target.value)} className="nv-input" />
          </F>
        </div>

        <div className="pt-2">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="label-caps text-cyan text-[10px]">Line items · adjust price here</div>
              <div className="text-[10px] text-white/40 mt-0.5">
                Tap any Rate field to change the price · totals update automatically.
              </div>
            </div>
            <button type="button" onClick={addLine}
              data-testid="bo-invoice-add-line"
              className="text-xs text-cyan hover:brightness-110 inline-flex items-center gap-1">
              <Plus size={11} /> Add line
            </button>
          </div>
          <div className="space-y-3">
            {form.line_items.map((li, idx) => {
              const rowTotal = (Number(li.quantity) || 0) * (Number(li.unit_price) || 0);
              return (
                <div key={idx} className="rounded-2xl bg-ink-950/60 border border-white/10 p-3 space-y-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <label className="flex-1 block">
                      <span className="label-caps text-[9px] text-white/50 block mb-1">Description</span>
                      <input
                        data-testid={`bo-invoice-line-desc-${idx}`}
                        value={li.description}
                        onChange={(e) => setLine(idx, { description: e.target.value })}
                        placeholder="e.g. Chauffeur ride to JFK"
                        className="nv-input w-full" />
                    </label>
                    <button type="button" onClick={() => removeLine(idx)}
                      disabled={form.line_items.length === 1}
                      aria-label="Remove line"
                      className="mt-6 w-8 h-8 rounded-full border border-white/15 text-white/50 hover:bg-red-500/20 hover:text-red-300 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0 flex items-center justify-center">
                      <X size={12} />
                    </button>
                  </div>
                  <div className="grid grid-cols-[1fr_auto_1fr] gap-2">
                    <label className="block">
                      <span className="label-caps text-[9px] text-white/50 block mb-1">
                        {li.unit === "day" ? "Days" : "Hours"}
                      </span>
                      <input
                        type="number" step="0.5" min="0"
                        inputMode="decimal"
                        data-testid={`bo-invoice-line-qty-${idx}`}
                        value={li.quantity}
                        onChange={(e) => setLine(idx, { quantity: e.target.value })}
                        className="nv-input w-full text-lg font-semibold" />
                    </label>
                    <label className="block">
                      <span className="label-caps text-[9px] text-white/50 block mb-1">Unit</span>
                      <select
                        data-testid={`bo-invoice-line-unit-${idx}`}
                        value={li.unit || "hour"}
                        onChange={(e) => setLine(idx, { unit: e.target.value })}
                        className="nv-input h-full px-2 text-sm font-medium">
                        <option value="hour">hrs</option>
                        <option value="day">days</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className="label-caps text-[9px] text-cyan block mb-1">
                        Rate ($/{li.unit === "day" ? "day" : "hr"}) · tap to edit
                      </span>
                      <input
                        type="number" step="0.01" min="0"
                        inputMode="decimal"
                        data-testid={`bo-invoice-line-price-${idx}`}
                        value={li.unit_price}
                        onChange={(e) => setLine(idx, { unit_price: e.target.value })}
                        onFocus={(e) => e.target.select()}
                        placeholder="0.00"
                        className="nv-input w-full text-lg font-semibold border-cyan/40" />
                    </label>
                  </div>
                  <div className="flex items-center justify-between text-[11px] pt-1">
                    <span className="text-white/40">Line total</span>
                    <span className="text-cyan font-semibold" data-testid={`bo-invoice-line-total-${idx}`}>
                      ${rowTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 pt-2">
          <F label="Discount ($)">
            <input type="number" step="0.01" min="0"
              inputMode="decimal"
              data-testid="bo-invoice-discount"
              value={form.discount}
              onChange={(e) => set("discount", e.target.value)}
              className="nv-input" />
          </F>
          <div className="rounded-2xl bg-ink-950/60 border border-cyan/20 p-4">
            <div className="flex justify-between text-xs text-white/60 py-0.5">
              <span>Subtotal</span><span>${totals.subtotal.toFixed(2)}</span>
            </div>
            {totals.discount > 0 && (
              <div className="flex justify-between text-xs text-white/60 py-0.5">
                <span>Discount</span><span>-${totals.discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-xs text-white/60 py-0.5">
              <span>Tax ({form.tax_rate}%)</span><span>${totals.tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-serif text-xl text-white pt-2 mt-2 border-t border-cyan/40">
              <span>Total</span><span data-testid="bo-invoice-total">${totals.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <F label="Notes (shown on the invoice)">
          <textarea rows={2} data-testid="bo-invoice-notes"
            value={form.notes} onChange={(e) => set("notes", e.target.value)}
            className="nv-input" placeholder="Booked over the phone. Cash on arrival." />
        </F>

        {/* Refundable deposit — rendered as its own section on the PDF so
            it never gets mistaken for the amount owed. Deposits from a
            "New from rentals" bundle pre-populate this automatically. */}
        <div className="rounded-2xl border border-lime-400/25 bg-lime-400/[0.03] p-4 space-y-2">
          <div className="flex items-center gap-2">
            <div className="label-caps text-[10px] text-lime-300">Refundable deposit</div>
            <span className="text-[10px] text-white/40">— shown as its own section on the PDF</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <F label="Amount ($)">
              <input
                type="number" step="0.01" min="0"
                inputMode="decimal"
                data-testid="bo-invoice-deposit-amount"
                value={form.deposit_amount}
                onChange={(e) => set("deposit_amount", e.target.value)}
                className="nv-input" />
            </F>
            <F label="Status">
              <select
                data-testid="bo-invoice-deposit-status"
                value={form.deposit_status}
                onChange={(e) => set("deposit_status", e.target.value)}
                className="nv-input">
                <option value="pending">Pending</option>
                <option value="held">Held</option>
                <option value="refunded">Refunded</option>
                <option value="kept">Kept (damage)</option>
              </select>
            </F>
            <F label="Note (optional)">
              <input
                type="text"
                data-testid="bo-invoice-deposit-note"
                value={form.deposit_note}
                onChange={(e) => set("deposit_note", e.target.value)}
                placeholder="Refunded within 72 hours"
                className="nv-input" />
            </F>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 py-2.5 rounded-full text-xs label-caps border border-white/12 text-white/60 hover:border-white/30">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            data-testid="bo-invoice-save"
            className="flex-1 py-2.5 rounded-full text-xs label-caps bg-cyan text-midnight font-semibold hover:brightness-110 disabled:opacity-50">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create invoice"}
          </button>
        </div>
        <style>{`
          .nv-input {
            background: #0f0f11; border: 1px solid rgba(255,255,255,0.08);
            color: #fff; border-radius: 10px; padding: 10px 12px;
            width: 100%; font-size: 15px;
          }
          .nv-input:focus { outline: none; border-color: #39D6FF; box-shadow: 0 0 0 3px rgba(57,214,255,0.15); }
        `}</style>
      </form>
    </div>
  );
}

/**
 * FromRentalsModal — build one consolidated invoice out of 1..N rental
 * bookings for the same customer. Search by name/email/phone, tick the
 * rentals to bundle, optionally expand the per-day breakdown, and click
 * Create. Backend auto-populates line items (days × daily rate + add-driver
 * + insurance + smoking fee) so the manager only tweaks tax/discount before
 * sending.
 */
function FromRentalsModal({ onClose, onCreated }) {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [expandPerDay, setExpandPerDay] = useState(false);
  const [taxRate, setTaxRate] = useState(8.875);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/rentals")
      .then((r) => setRentals(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Failed to load rentals"))
      .finally(() => setLoading(false));
  }, []);

  // Group rentals by customer key so managers can see "this client has 3
  // rentals" and check them off together. Uses lowercased email as the
  // grouping key (phone fallback for guests without an email).
  const grouped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const groups = new Map();
    for (const r of rentals) {
      // Show any rental that isn't cancelled / denied / pending_approval — a
      // manager should be able to bill anything that's been priced (including
      // pending_payment reservations that were paid off-website in cash).
      if (["cancelled", "denied", "pending_approval"].includes(r.status)) continue;
      const email = (r.customer_email || r.email || "").toLowerCase();
      const phone = (r.customer_phone || r.phone || "");
      const key = email || `p:${phone}` || r.id;
      const name = (r.full_name || "").toLowerCase();
      if (needle && !(email.includes(needle) || phone.toLowerCase().includes(needle) || name.includes(needle))) continue;
      const g = groups.get(key) || {
        key,
        name: r.full_name,
        email: r.customer_email || r.email,
        phone: r.customer_phone || r.phone,
        rentals: [],
      };
      g.rentals.push(r);
      groups.set(key, g);
    }
    return [...groups.values()].sort((a, b) => (b.rentals.length - a.rentals.length));
  }, [rentals, q]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const create = async () => {
    if (selected.size === 0) return toast.error("Select at least one rental");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invoices/from-rentals", {
        booking_ids: [...selected],
        expand_per_day: expandPerDay,
        tax_rate: Number(taxRate) || 0,
      });
      toast.success(`Invoice ${data.invoice.invoice_number} created from ${selected.size} rental${selected.size === 1 ? "" : "s"}`);
      onCreated?.(data.invoice);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to build invoice");
    } finally { setBusy(false); }
  };

  const selectedFrom = (group) => group.rentals.filter((r) => selected.has(r.id)).length;

  return (
    <div data-testid="bo-invoice-from-rentals-modal"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-ink-950/85 flex items-start justify-center p-4 overflow-y-auto"
    >
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-ink-900 border border-cyan/40 rounded-3xl p-6 my-8 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-caps text-cyan text-xs">Build invoice from rentals</div>
            <div className="text-white font-serif text-xl mt-0.5">Bundle multi-day rentals into one invoice</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X size={16} /></button>
        </div>

        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="search"
            data-testid="bo-invoice-rentals-search"
            placeholder="Search customer name / email / phone…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full bg-ink-800 border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm text-white outline-none focus:border-cyan/50"
          />
        </div>

        <div className="max-h-[45vh] overflow-y-auto pr-1 space-y-2">
          {loading ? (
            <div className="text-white/50 text-xs text-center py-6">Loading rentals…</div>
          ) : grouped.length === 0 ? (
            <div className="text-white/50 text-xs text-center py-6">
              {q ? "No rentals match that search." : "No confirmed/active/completed rentals to invoice yet."}
            </div>
          ) : (
            grouped.map((g) => (
              <div key={g.key} className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{g.name || "Unnamed"}</div>
                    <div className="text-[10px] text-white/50 truncate">
                      {g.email || g.phone || "no contact"} · {g.rentals.length} rental{g.rentals.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  {selectedFrom(g) > 0 && (
                    <span className="text-[10px] label-caps px-2 py-0.5 rounded-full bg-cyan/20 text-cyan border border-cyan/40">
                      {selectedFrom(g)} selected
                    </span>
                  )}
                </div>
                <div className="space-y-1">
                  {g.rentals.map((r) => (
                    <label key={r.id}
                      data-testid={`bo-invoice-rental-${r.id}`}
                      className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs cursor-pointer transition ${
                        selected.has(r.id) ? "bg-cyan/10 border border-cyan/40" : "hover:bg-white/[0.04] border border-transparent"
                      }`}>
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => toggle(r.id)}
                        className="w-4 h-4 accent-cyan"
                      />
                      <Car size={11} className="text-cyan shrink-0" />
                      <span className="text-white truncate flex-1">
                        {r.vehicle_name} · {r.days} day{r.days === 1 ? "" : "s"}
                      </span>
                      <span className="text-white/50 text-[10px] whitespace-nowrap">
                        {r.start_at ? new Date(r.start_at).toLocaleDateString() : "—"}
                      </span>
                      <span className="text-gold whitespace-nowrap">${Number(r.amount || 0).toFixed(0)}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2">
            <input
              type="checkbox"
              data-testid="bo-invoice-expand-per-day"
              checked={expandPerDay}
              onChange={(e) => setExpandPerDay(e.target.checked)}
              className="w-4 h-4 accent-cyan"
            />
            <div className="text-xs">
              <div className="text-white">Expand per-day rows</div>
              <div className="text-[10px] text-white/50">One row per date (Day 1 · Fri Mar 3 · $325)</div>
            </div>
          </label>
          <F label="Tax rate %">
            <input
              type="number"
              step="0.001"
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
              data-testid="bo-invoice-tax-rate"
              className="nv-input" />
          </F>
        </div>

        <div className="flex gap-2 pt-2 border-t border-white/10">
          <button type="button" onClick={onClose}
            className="flex-1 py-2.5 rounded-full text-xs label-caps border border-white/20 text-white/70 hover:bg-white/5">
            Cancel
          </button>
          <button type="button" onClick={create} disabled={busy || selected.size === 0}
            data-testid="bo-invoice-from-rentals-create"
            className="flex-1 py-2.5 rounded-full text-xs label-caps bg-cyan text-midnight font-semibold hover:brightness-110 disabled:opacity-40">
            {busy ? "Building…" : `Create invoice from ${selected.size || 0} rental${selected.size === 1 ? "" : "s"}`}
          </button>
        </div>

        <style>{`
          .nv-input {
            background: #0f0f11; border: 1px solid rgba(255,255,255,0.08);
            color: #fff; border-radius: 10px; padding: 10px 12px;
            width: 100%; font-size: 15px;
          }
          .nv-input:focus { outline: none; border-color: #39D6FF; box-shadow: 0 0 0 3px rgba(57,214,255,0.15); }
        `}</style>
      </div>
    </div>
  );
}



/* ---------------- Convert Invoice → Booking ---------------- */
function ConvertInvoiceModal({ invoice, onClose, onDone }) {
  // Prefill defaults from the invoice
  const svcISO = invoice.service_date || "";
  const invoiceDeposit = Number(invoice.deposit_amount) || 0;
  const invoiceHeld = ["held", "kept"].includes(invoice.deposit_status);
  const lineItemCount = (invoice.line_items || []).length;
  const canSplit = lineItemCount >= 2;
  const [form, setForm] = useState({
    booking_type: "ride",
    estimated_duration_hours: 3,
    passenger_count: 1,
    pickup: invoice.pickup_location || "",
    dropoff: "",
    trip_purpose: "other",
    // Rental defaults
    start_at: svcISO ? svcISO.slice(0, 16) : "",
    end_at:   svcISO ? (() => {
      try { const d = new Date(svcISO); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 16); }
      catch { return ""; }
    })() : "",
    return_location: invoice.pickup_location || "",
    keep_deposit_held: invoiceHeld,
    // Multi-day split — default off; only surfaces when the invoice has 2+ line items.
    split_by_line_items: false,
  });
  const [busy, setBusy] = useState(false);
  const [autoApprove, setAutoApprove] = useState(false);
  const [prefLoaded, setPrefLoaded] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isRide = form.booking_type === "ride";
  const showDepositRow = !isRide && invoiceDeposit > 0;

  // Pull the manager's default so the toggle starts in the right position.
  useEffect(() => {
    api.get("/admin/me/preferences")
      .then((r) => setAutoApprove(!!r.data?.auto_approve_invoice_conversions))
      .catch(() => {})
      .finally(() => setPrefLoaded(true));
  }, []);

  // Persist the toggle in the background — so next time it defaults the same.
  const flipAutoApprove = async (next) => {
    setAutoApprove(next);
    try {
      await api.patch("/admin/me/preferences",
        { auto_approve_invoice_conversions: next });
    } catch { /* non-blocking */ }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        booking_type: form.booking_type,
        auto_approve: autoApprove,
        split_by_line_items: canSplit && !!form.split_by_line_items,
      };
      if (isRide) {
        body.estimated_duration_hours = Number(form.estimated_duration_hours) || 3;
        body.passenger_count = Number(form.passenger_count) || 1;
        body.pickup = form.pickup || undefined;
        body.dropoff = form.dropoff || undefined;
        body.trip_purpose = form.trip_purpose || undefined;
      } else {
        body.start_at = form.start_at ? new Date(form.start_at).toISOString() : undefined;
        body.end_at   = form.end_at   ? new Date(form.end_at).toISOString()   : undefined;
        body.pickup = form.pickup || undefined;
        body.return_location = form.return_location || form.pickup || undefined;
        // Rental-only deposit lifecycle switch
        if (invoiceDeposit > 0) {
          body.keep_deposit_held = !!form.keep_deposit_held;
        }
      }
      const r = await api.post(`/admin/invoices/${invoice.id}/convert-to-booking`, body);
      const count = r?.data?.count || 1;
      const bkId = r?.data?.booking?.id;
      const status = r?.data?.booking?.status;
      const isSplit = r?.data?.split;
      toast.success(
        isSplit
          ? (status === "confirmed"
              ? `Created ${count} confirmed bookings on the calendar`
              : `Created ${count} pending bookings — approve them from Schedule`)
          : (status === "confirmed"
              ? "Booking created & confirmed — live on the calendar"
              : "Booking created — pending approval")
      );
      if (bkId && typeof window !== "undefined") {
        window.__lastConvertedBookingId = bkId;
      }
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
         onClick={onClose} data-testid="convert-invoice-modal">
      <div className="glass-strong rounded-3xl border border-amber-400/40 shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="label-caps text-amber-200 text-[10px]">Convert to booking</div>
            <h3 className="font-serif text-xl mt-0.5">{invoice.invoice_number}</h3>
            <div className="text-xs text-white/50 mt-0.5">
              {invoice.customer_name} · ${(invoice.totals?.total || 0).toFixed(2)}
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-2 rounded-full hover:bg-white/5">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className={`rounded-xl px-3 py-2 text-[11px] border ${
            autoApprove
              ? "bg-emerald-500/[0.08] border-emerald-400/30 text-white/75"
              : "bg-cyan/[0.06] border-cyan/25 text-white/70"
          }`}>
            {autoApprove ? (
              <>Lands as <span className="text-emerald-300 font-bold">confirmed</span> — locked to the calendar the second you tap Convert.</>
            ) : (
              <>Lands as <span className="text-amber-200 font-bold">pending approval</span> — tap Confirm on the Schedule
                 or Bookings tab to lock it.</>
            )}
            {" "}The invoice stays as-is (no lock).
          </div>

          {/* Auto-approve toggle — per-manager preference, saved automatically */}
          <label className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/30 px-3 py-2 cursor-pointer select-none"
                 data-testid="convert-autoapprove-row">
            <input type="checkbox"
              data-testid="convert-autoapprove"
              checked={autoApprove}
              disabled={!prefLoaded}
              onChange={(e) => flipAutoApprove(e.target.checked)}
              className="h-4 w-4 accent-emerald-400" />
            <div className="flex-1">
              <div className="text-sm text-white">Auto-approve my conversions</div>
              <div className="text-[10px] text-white/45">
                Saved to your profile. When on, converted invoices skip pending and go straight onto the calendar.
              </div>
            </div>
          </label>

          {/* Multi-day / multi-line-item split — appears only when 2+ line items exist */}
          {canSplit && (
            <label className="flex items-start gap-3 rounded-xl border border-cyan/25 bg-cyan/[0.05] px-3 py-2 cursor-pointer select-none"
                   data-testid="convert-split-row">
              <input type="checkbox"
                data-testid="convert-split"
                checked={!!form.split_by_line_items}
                onChange={(e) => set("split_by_line_items", e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-cyan" />
              <div className="flex-1">
                <div className="text-sm text-white">
                  Split into <span className="text-cyan font-bold">{lineItemCount} bookings</span> — one per line item
                </div>
                <div className="text-[10px] text-white/45 mt-0.5">
                  {form.split_by_line_items
                    ? (isRide
                        ? "Each ride shifts by the hours you set below. Deposit lands only on the first booking."
                        : "Each booking runs one day starting from the invoice's service date. Deposit lands only on the first booking.")
                    : "Off — the invoice becomes one single booking for the whole amount."}
                </div>
              </div>
            </label>
          )}

          <div>
            <div className="label-caps text-[10px] text-white/50 mb-1">Booking type</div>
            <div className="flex gap-2">
              {[["ride", "Chauffeur"], ["rental", "Self-drive"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => set("booking_type", k)}
                  data-testid={`convert-btype-${k}`}
                  className={`flex-1 py-2 rounded-lg text-xs label-caps border transition ${
                    form.booking_type === k
                      ? "bg-amber-400/20 text-amber-100 border-amber-300/50"
                      : "bg-black/30 text-white/60 border-white/10 hover:border-white/25"
                  }`}>{l}</button>
              ))}
            </div>
          </div>

          {isRide ? (
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-1">
                <div className="label-caps text-[10px] text-white/50 mb-1">Hours</div>
                <input type="number" min="1" step="0.5" value={form.estimated_duration_hours}
                  data-testid="convert-hours"
                  onChange={(e) => set("estimated_duration_hours", e.target.value)}
                  className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
              </label>
              <label className="col-span-1">
                <div className="label-caps text-[10px] text-white/50 mb-1">Passengers</div>
                <input type="number" min="1" value={form.passenger_count}
                  onChange={(e) => set("passenger_count", e.target.value)}
                  className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
              </label>
              <label className="col-span-2">
                <div className="label-caps text-[10px] text-white/50 mb-1">Pickup</div>
                <input type="text" value={form.pickup} onChange={(e) => set("pickup", e.target.value)}
                  data-testid="convert-pickup"
                  placeholder="123 Main St, NYC"
                  className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
              </label>
              <label className="col-span-2">
                <div className="label-caps text-[10px] text-white/50 mb-1">Drop-off</div>
                <input type="text" value={form.dropoff} onChange={(e) => set("dropoff", e.target.value)}
                  placeholder="Optional"
                  className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
              </label>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-1">
                <div className="label-caps text-[10px] text-white/50 mb-1">Pickup date/time</div>
                <input type="datetime-local" value={form.start_at}
                  data-testid="convert-start"
                  onChange={(e) => set("start_at", e.target.value)}
                  className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
              </label>
              <label className="col-span-1">
                <div className="label-caps text-[10px] text-white/50 mb-1">Return date/time</div>
                <input type="datetime-local" value={form.end_at}
                  data-testid="convert-end"
                  onChange={(e) => set("end_at", e.target.value)}
                  className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
              </label>
              <label className="col-span-2">
                <div className="label-caps text-[10px] text-white/50 mb-1">Pickup location</div>
                <input type="text" value={form.pickup} onChange={(e) => set("pickup", e.target.value)}
                  className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none" />
              </label>
              {showDepositRow && (
                <div className="col-span-2"
                     data-testid="convert-keep-deposit-row">
                  <label className="flex items-start gap-3 rounded-xl border border-amber-400/25 bg-amber-500/[0.06] px-3 py-2 cursor-pointer select-none">
                    <input type="checkbox"
                      data-testid="convert-keep-deposit"
                      checked={!!form.keep_deposit_held}
                      onChange={(e) => set("keep_deposit_held", e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-amber-400" />
                    <div className="flex-1">
                      <div className="text-sm text-white">
                        Keep the <span className="text-amber-200 font-bold">${invoiceDeposit.toFixed(0)}</span> deposit held on the new booking
                      </div>
                      <div className="text-[10px] text-white/50 mt-0.5">
                        {form.keep_deposit_held
                          ? "Deposit stays on the customer's card and sits in the Deposits ledger until you release, apply, or forfeit it."
                          : "Deposit is released — the new booking starts with $0 held. Use this if you already refunded or applied the deposit on the invoice."}
                      </div>
                    </div>
                  </label>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-5 pt-2 border-t border-white/10 flex gap-2">
          <button type="button" onClick={onClose}
            className="flex-1 py-2.5 rounded-full text-xs label-caps border border-white/20 text-white/70 hover:bg-white/5">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={busy}
            data-testid="convert-submit"
            className="flex-1 py-2.5 rounded-full text-xs label-caps bg-amber-400 text-black font-bold hover:brightness-110 disabled:opacity-40 inline-flex items-center justify-center gap-1.5">
            <CalendarPlus size={12} />
            {busy ? "Converting…" : "Convert & add to calendar"}
          </button>
        </div>
      </div>
    </div>
  );
}
